import React from 'react';

import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { phoneFormat } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { timeFormat } from '@common/react/utils/timeUtils';
import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';
import GoogleMap from '@common/react/components/google/GoogleMap/GoogleMap';
import { getGoogleMapLink } from '@common/react/components/google/googleUtils';

import { AmenityTypeNames } from '@commonTuna/react/objects/Amenity';
import { AmenityTypeIcons } from '@commonTuna/react/objects/AmenityImages';

import { solveWorkingHours } from '@commonTuna/react/utils/workingHours/workingHours';

import { Location } from '@app/objects/Location';

import '@app/scss/googleMap.scss';

interface Props {
	location: Location;
}

const Contacts: React.FC<Props> = ({ location }) => {
	const link = React.useMemo(() => {
		return getGoogleMapLink(location.addressEn);
	}, [location]);
	const arr = React.useMemo(() =>
		[location.addressEn, location.city, location.state, location.zip].filter((str) => str), []);
	const address = arr.filter((e) => e).join(', ');

	const onClick = (e) => {
		const el = e?.target;
		if (el?.closest('.gm-control-active') || el?.classList.contains('gm-control-active')
			|| el.tagName?.toLowerCase() === 'a') {
			e.preventDefault();
		}
	};

	const workingHours = React.useMemo(() => solveWorkingHours(location.workingHours), [location.workingHours]);

	return (
		<div className="map-container">
			<div className="contacts__location-wrapper">
				<h3 className="show-mobile">
					{location.companyName}
					{' - '}
					{location.nameEn}
				</h3>
				{location.files?.length ? <div className="contacts__location-images">
					<Swiper
						pagination={{
							clickable: true,
						}}
						modules={[Pagination]}
						className="mySwiper"
						autoHeight
						observer
						observeParents
						observeSlideChildren
					>
						{location.files.map((item) => <SwiperSlide key={item.id}>
							<ImageLazy
								alt={`clinic ${item.id}`}
								width="300px"
								height="300px"
								className="slider-image"
								src={`/remote/${item.file.src}`}
							/>
						</SwiperSlide>)}
					</Swiper>
				</div> : null}
				<div className="clearfix contacts__location">
					<div className="location-name clearfix">
						<h3 className="hide-mobile">
							{location.companyName}
							{' - '}
							{location.nameEn}
						</h3>
						<div className="clearfix">
							<h4 className="pull-left">
								<a
									onClick={onClick}
									title={`Open ${location.companyName} address at google maps`}
									target="_blank"
									href={link}
									rel="noreferrer"
								>
									<label>Address:</label>
									<address>{address}</address>
								</a>
							</h4>
							{(location.phone || location.altPhone) && (
								<h4 className="pull-left">
									{location.phone && (
										<a href={`tel:${location.phone}`} title={`Call ${location.companyName} Clinic`}>
											<label>Phone:</label>
											{' '}
											{phoneFormat(location.phone)}
										</a>
									)}
									{location.altPhone && (<>
										{', '}
										<a href={`tel:${location.altPhone}`} title={`Call ${location.companyName} Clinic`}>
											{phoneFormat(location.altPhone)}
										</a>
									</>)}
								</h4>
							)}
							{location.portalEmail && <h4 className="pull-left">
								<a href={`mailto:${location.portalEmail}`} title={`Send Email to ${location.companyName} Clinic`}>
									<label>Email:</label>
									{' '}
									{location.portalEmail}
								</a>
							</h4>}
						</div>
					</div>
					{location.workingHours && <div className="mr10 working-hours">
						<h4><label>Hours: </label></h4>
						<div>
							<ul className="working-hour-list">
								{
									workingHours
										.map((wh) => <li className="working-hour" key={wh.id}>
											<div className="text-center">
												{wh.title}
											</div>
											<div className="text-center">
												{timeFormat(wh.startTime)}
												{' - '}
												{timeFormat(wh.endTime)}
											</div>
										</li>)
								}
							</ul>
						</div>
					</div>}
					{location.amenities.length > 0 ? <div className="amenities mb10 mt10">
						{location.amenities.map(({ amenityType }) => (
							<div key={amenityType} className="amenitiy">
								<img
									width="48px"
									height="48px"
									alt={AmenityTypeNames[amenityType]}
									title={AmenityTypeNames[amenityType]}
									src={AmenityTypeIcons[amenityType]}
								/>
							</div>
						))}
					</div> : null}
				</div>
			</div>
			<a
				onClick={onClick}
				target="_blank"
				href={link}
				rel="noreferrer"
			>
				<GoogleMap address={location.addressEn} />
			</a>
		</div>
	);
};

export default Contacts;
